import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Box, Button, Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import {
  RadioButtonProps,
  RadioGroup,
  RadioGroupChangeEvent,
} from '@progress/kendo-react-inputs';

import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { sendCommandResponse } from '../store/Alerts/AlertsActions';
import { CleanWeightRecord } from '../Interfaces/Alerts/CommandResponse';
import {
  WeightAlertGroupRecord,
  WeightAlertRecord,
} from '../Interfaces/Alerts/AlertRecord';
import { CLEAN_WGT_RECORDS_API } from '../Interfaces/Alerts/Api';
import { ApiEnums } from '../Interfaces/ApiEnums';
import { alertTypeNameMap, getWeightCategoryText } from '../utils/textMappings';
import { GetString } from './StringResourceManager';
import { fetchAlertByID } from './AlertsGrid/AlertsGrid';
import { ApplicationState } from '../store';

const useStyles = makeStyles(theme => ({
  gridAlign: {
    display: 'flex',
    alignItems: 'center',
  },
  margin: {
    margin: theme.spacing(1),
  },
  textField: {
    width: '25ch',
  },
  box: {
    height: 60,
    display: 'flex',
    padding: theme.spacing(2),
    margin: theme.spacing(1),
  },
  spreadBox: {
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  dialogAction: {
    height: '3rem',
    padding: theme.spacing(3),
  },
  dialogLabel: {
    fontSize: '1.1rem',
    display: 'inline',
  },
  dialogTextBold: {
    fontWeight: 'bold',
  },
  radioButtonLabel: {
    fontSize: '1.05rem',
    display: 'inline',
  },
  breakWord: {
    wordWrap: 'break-word',
  },
}));

interface Props {
  alert: WeightAlertGroupRecord;
  closeContainer: () => void;
}

const CleanWeightRecordForm: React.FC<Props> = ({ alert, closeContainer }) => {
  const classes = useStyles();
  const [weightCategory, setWeightCategory] = useState<ApiEnums.WeightCategory>(
    alert.AlertType === ApiEnums.AlertType.WeightOverridden
      ? ApiEnums.WeightCategory.Mixed
      : alert.WeightCategory
  );

  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token
  );

  const [referenceAlert, setReferenceAlert] =
    useState<WeightAlertRecord>(Object);

  const dispatch: Dispatch<any> = useDispatch();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    // Preventing the page from reloading
    event.preventDefault();

    let payload: CleanWeightRecord[] = [];
    alert.AlertIDs.forEach(alertID => {
      payload.push({
        AlertID: alertID,
        WeightCategory: weightCategory,
      });
    });

    sendCommandResponse(dispatch, CLEAN_WGT_RECORDS_API, payload);
  };

  useEffect((): ReturnType<React.EffectCallback> => {
    if (alert.AlertIDs.length > 0) {
      fetchAlertByID(alert.AlertIDs[0], token)
        .then(data => {
          setReferenceAlert(data);
        })
        .catch(error => {
          console.error(error);
        });
    }
    return (): void => {};
  }, []);

  const onWeightCategoryChanged = (event: RadioGroupChangeEvent) => {
    setWeightCategory(event.value);
  };

  const weightCategories = new Array<RadioButtonProps>(
    {
      label: GetString('REMOVE_ALL_WEIGHTS'),
      value: ApiEnums.WeightCategory.Mixed,
      disabled: false,
    },
    {
      label: GetString('KEEP_LIGHT_WEIGHTS_ONLY'),
      value: ApiEnums.WeightCategory.Light,
      disabled: alert.AlertType === ApiEnums.AlertType.WeightOverridden,
    },
    {
      label: GetString('KEEP_MEDIUM_WEIGHTS_ONLY'),
      value: ApiEnums.WeightCategory.Medium,
      disabled: alert.AlertType === ApiEnums.AlertType.WeightOverridden,
    },
    {
      label: GetString('KEEP_HEAVY_WEIGHTS_ONLY'),
      value: ApiEnums.WeightCategory.Heavy,
      disabled: alert.AlertType === ApiEnums.AlertType.WeightOverridden,
    }
  );

  return (
    <form autoComplete="off" noValidate onSubmit={handleSubmit}>
      <Container>
        <Grid
          container
          spacing={2}
          className={clsx(classes.gridAlign, classes.margin)}
        >
          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={clsx(classes.dialogLabel, classes.dialogTextBold)}
              gutterBottom
              color="primary"
            >
              {GetString('ITEM_DETAILS')}
            </Typography>
          </Grid>

          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('UPDATE_WGT_RECORD_ITEM_NAME')}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(classes.dialogLabel, classes.dialogTextBold)}
              gutterBottom
            >
              {alert.ItemDesc}
            </Typography>
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('UPDATE_WGT_RECORD_ITEM_BARCODE')}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(
                classes.dialogLabel,
                classes.dialogTextBold,
                classes.breakWord,
                classes.textField
              )}
              gutterBottom
            >
              {alert.Barcode}
            </Typography>
          </Grid>

          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('UPDATE_WGT_RECORD_WGTS')} (
              {GetString('UPDATE_WGT_RECORD_STANDARD_WGT_UNIT')}):{' '}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(
                classes.dialogLabel,
                classes.dialogTextBold,
                classes.breakWord,
                classes.textField
              )}
              gutterBottom
            >
              {referenceAlert?.WeightRecord?.join(', ') ?? ''}
            </Typography>
          </Grid>

          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('RECOMMENDED_WEIGHT')} (
              {GetString('UPDATE_WGT_RECORD_STANDARD_WGT_UNIT')}):{' '}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(classes.dialogLabel, classes.dialogTextBold)}
              gutterBottom
            >
              {alert.ReferenceWeight >= 0 ? alert.ReferenceWeight : 'N/A'}
            </Typography>
          </Grid>

          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('WEIGHT_CATEGORY')}:{' '}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(classes.dialogLabel, classes.dialogTextBold)}
              gutterBottom
            >
              {getWeightCategoryText(alert.WeightCategory)}
            </Typography>
          </Grid>

          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={classes.dialogLabel}
              gutterBottom
            >
              {GetString('ALERT_TYPE')}:{' '}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(classes.dialogLabel, classes.dialogTextBold)}
              gutterBottom
            >
              {GetString(alertTypeNameMap(alert.AlertType))}
            </Typography>
          </Grid>

          <Grid item md={12} xs={12}>
            <Divider />
          </Grid>

          <Grid item md={12} xs={12}>
            <Typography
              variant="body1"
              className={clsx(classes.dialogLabel, classes.dialogTextBold)}
              gutterBottom
              color="primary"
            >
              {GetString('SELECT_WEIGHT_CATEGORY')}
            </Typography>
          </Grid>

          <Grid item md={12} xs={12}>
            <RadioGroup
              className={clsx(classes.radioButtonLabel)}
              data={weightCategories}
              defaultValue={weightCategory}
              onChange={onWeightCategoryChanged}
            />
          </Grid>
        </Grid>
        <Divider />
        <Box className={clsx(classes.spreadBox, classes.box)}>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            className={classes.dialogAction}
            onClick={closeContainer}
          >
            {GetString('UPDATE_WGT_RECORD_ACTION_1')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.dialogAction}
            onClick={closeContainer}
          >
            {GetString('UPDATE_WGT_RECORD_ACTION_2')}
          </Button>
        </Box>
      </Container>
    </form>
  );
};

export default CleanWeightRecordForm;
